import React, { createContext, useContext, useState, useEffect } from 'react';
import { Fund, WishlistContextType } from '../types';
import { Stock } from '../types/stock';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [fundWishlist, setFundWishlist] = useState<Fund[]>([]);
  const [stockWishlist, setStockWishlist] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);

  // Load wishlist from localStorage when user logs in
  useEffect(() => {
    const loadWishlist = async () => {
      if (!user) {
        setFundWishlist([]);
        setStockWishlist([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Load from localStorage
        const savedFunds = localStorage.getItem(`fund_wishlist_${user.uid}`);
        const savedStocks = localStorage.getItem(`stock_wishlist_${user.uid}`);
        
        setFundWishlist(savedFunds ? JSON.parse(savedFunds) : []);
        setStockWishlist(savedStocks ? JSON.parse(savedStocks) : []);
      } catch (error) {
        console.error('Failed to load wishlist:', error);
        toast.error('Failed to load your wishlist');
      } finally {
        setLoading(false);
      }
    };

    loadWishlist();
  }, [user]);

  // Save wishlist to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem(`fund_wishlist_${user.uid}`, JSON.stringify(fundWishlist));
      localStorage.setItem(`stock_wishlist_${user.uid}`, JSON.stringify(stockWishlist));
    }
  }, [fundWishlist, stockWishlist, user]);

  const addToWishlist = async (item: Fund | Stock) => {
    if (!user) {
      toast.error('Please log in to add items to your wishlist');
      return;
    }

    try {
      // Determine item type
      const isFund = 'schemeCode' in item;
      
      // Update local state
      if (isFund) {
        setFundWishlist(prev => {
          // Check if item already exists
          const exists = prev.some(fund => fund.schemeCode === (item as Fund).schemeCode);
          if (exists) return prev;
          return [...prev, item as Fund];
        });
        toast.success('Fund added to wishlist');
      } else {
        setStockWishlist(prev => {
          // Check if item already exists
          const exists = prev.some(stock => stock.symbol === (item as Stock).symbol);
          if (exists) return prev;
          return [...prev, item as Stock];
        });
        toast.success('Stock added to wishlist');
      }
    } catch (error) {
      console.error('Add to wishlist error:', error);
      toast.error('Failed to add to wishlist');
    }
  };

  const removeFromWishlist = async (id: number | string, type: 'fund' | 'stock') => {
    if (!user) return;

    try {
      // Update local state
      if (type === 'fund') {
        setFundWishlist(prev => prev.filter(fund => fund.schemeCode !== id));
        toast.success('Removed from wishlist');
      } else {
        setStockWishlist(prev => prev.filter(stock => stock.symbol !== id));
        toast.success('Removed from wishlist');
      }
    } catch (error) {
      console.error('Remove from wishlist error:', error);
      toast.error('Failed to remove from wishlist');
    }
  };

  const isInWishlist = async (id: number | string, type: 'fund' | 'stock') => {
    if (!user) return false;

    // Check local state
    if (type === 'fund') {
      return fundWishlist.some(fund => fund.schemeCode === id);
    } else {
      return stockWishlist.some(stock => stock.symbol === id);
    }
  };

  return (
    <WishlistContext.Provider
      value={{ 
        fundWishlist, 
        stockWishlist, 
        addToWishlist, 
        removeFromWishlist, 
        isInWishlist,
        loading
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
}